/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateStaffUserSchema,
  STAFF_ROLE_VALUES,
  type CreateStaffUserInput,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type CreatedStaffUser } from '../../lib/api/sa.js';
import { useCreateStaffUser } from '../../lib/queries/sa.js';

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

interface StaffUserFormProps {
  readonly onCreated: (result: CreatedStaffUser) => void;
}

/** Create a Product Admin account (Figma-less staff console). Shared-Zod validated. */
export function StaffUserForm({ onCreated }: StaffUserFormProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateStaffUser();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateStaffUserInput>({
    resolver: zodResolver(CreateStaffUserSchema),
    defaultValues: { role: 'SUPPORT' },
  });

  const submit = useCallback(
    async (values: CreateStaffUserInput): Promise<void> => {
      const result = await create.mutateAsync(values);
      onCreated(result);
    },
    [create, onCreated],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <FormField
        id="staff-name"
        type="text"
        label={t('sa.staff.form.fullName')}
        autoComplete="off"
        error={errors.fullName?.message}
        registration={register('fullName')}
      />
      <FormField
        id="staff-email"
        type="email"
        label={t('sa.staff.form.email')}
        autoComplete="off"
        error={errors.email?.message}
        registration={register('email')}
      />
      <div>
        <label htmlFor="staff-role" className={LABEL_CLASS}>
          {t('sa.staff.form.role')}
        </label>
        <select id="staff-role" className={SELECT_CLASS} {...register('role')}>
          {STAFF_ROLE_VALUES.map((role) => (
            <option key={role} value={role}>
              {t(`sa.staff.role.${role}`)}
            </option>
          ))}
        </select>
      </div>

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('sa.staff.form.error')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('sa.staff.form.submit')}
        </Button>
      </div>
    </form>
  );
}
